import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Fade
} from "@mui/material";

import CardVehiculos from "../components/mapa/CardVehiculos";
import MostrarMapa from "../components/mapa/MostrarMapa";

import api from "../assets/utils/axiosConfig";

import SearchIcon from "@mui/icons-material/Search";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import PushPinIcon from "@mui/icons-material/PushPin";

// Función aux para distancia acumulada (Haversine en Km)
function calcularDistanciaKm(lat1, lon1, lat2, lon2) {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
    Math.cos((lat2 * Math.PI) / 180) *
    Math.sin(dLon / 2) *
    Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

const MapaPage = () => {
  const [vehiculos, setVehiculos] = useState([]);
  const [busqueda, setBusqueda] = useState("");
  const [centroMapa, setCentroMapa] = useState({
    lat: -33.45,
    lng: -70.65,
  });

  const [vehiculoSeleccionadoId, setVehiculoSeleccionadoId] = useState(null);
  const [viajeEnCurso, setViajeEnCurso] = useState(null); // 👈 ESTADO VIAJE EN CURSO
  const [modoPanel, setModoPanel] = useState("compacto");

  // 1️⃣ Consulta recurrente de vehículos + Inyección de puntos a la Polyline en tiempo real
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await api.post(process.env.REACT_APP_API_MAPA_VEHICULOS, {});
        const data = Array.isArray(response.data) ? response.data : [];

        const vehiculosFormateados = data.map((v) => ({
          ...v,
          latitude: Number(v.latitude),
          longitude: Number(v.longitude),
        }));

        setVehiculos(vehiculosFormateados);

        // 🟢 Si hay un vehículo seleccionado, actualizamos la ruta en vivo
        if (vehiculoSeleccionadoId) {
          const vehiculoActivo = vehiculosFormateados.find(
            (v) => v.idVehiculo === vehiculoSeleccionadoId
          );

          if (vehiculoActivo && vehiculoActivo.latitude && vehiculoActivo.longitude) {
            setViajeEnCurso((prevViaje) => {
              if (!prevViaje || !prevViaje.in_trip) return prevViaje;

              const puntos = prevViaje.trip.points;
              const ultimoPunto = puntos[puntos.length - 1];

              // Si avanzó respecto a la coordenada previa
              if (
                ultimoPunto.lat !== vehiculoActivo.latitude ||
                ultimoPunto.lng !== vehiculoActivo.longitude
              ) {
                const nuevoPunto = {
                  lat: vehiculoActivo.latitude,
                  lng: vehiculoActivo.longitude,
                  timestamp: vehiculoActivo.timeRegistration,
                  velocidad: vehiculoActivo.vel,
                };

                const distIncremento = calcularDistanciaKm(
                  ultimoPunto.lat,
                  ultimoPunto.lng,
                  nuevoPunto.lat,
                  nuevoPunto.lng
                );

                return {
                  ...prevViaje,
                  trip: {
                    ...prevViaje.trip,
                    distance_km: Number((prevViaje.trip.distance_km + distIncremento).toFixed(2)),
                    current_speed: vehiculoActivo.vel,
                    max_speed: Math.max(prevViaje.trip.max_speed, vehiculoActivo.vel),
                    last_point: {
                      lat: nuevoPunto.lat,
                      lng: nuevoPunto.lng,
                      rumbo: vehiculoActivo.rumbo,
                    },
                    points: [...puntos, nuevoPunto], // Suma el nuevo punto
                  },
                };
              }

              return prevViaje;
            });
          }
        }
      } catch (err) {
        console.error("Error al obtener vehículos:", err);
        setVehiculos([]);
      }
    };

    fetchData();
    const interval = setInterval(fetchData, 10000);
    return () => clearInterval(interval);
  }, [vehiculoSeleccionadoId]);

  // Centrar mapa según vehículo seleccionado
  useEffect(() => {
    if (!vehiculoSeleccionadoId) return;

    const v = vehiculos.find(
      (x) => x.idVehiculo === vehiculoSeleccionadoId
    );

    if (v?.latitude && v?.longitude) {
      setCentroMapa({
        lat: v.latitude,
        lng: v.longitude,
      });
    }
  }, [vehiculos, vehiculoSeleccionadoId]);

  // 2️⃣ Carga de ruta inicial llamando a ObtenerUltimoViaje.php cuando se selecciona un vehículo
  useEffect(() => {
    if (!vehiculoSeleccionadoId) {
      setViajeEnCurso(null);
      return;
    }

    const fetchUltimoViaje = async () => {
      try {
        // Limpiamos la ruta previa mientras carga
        setViajeEnCurso(null);

        const response = await api.post("/Mapa/ObtenerUltimoViaje.php", {
          vehiculo_id: vehiculoSeleccionadoId,
        });

        if (response.data && response.data.in_trip) {
          setViajeEnCurso(response.data);
        } else {
          setViajeEnCurso(null);
        }
      } catch (err) {
        console.error("Error al obtener el viaje en curso:", err);
        setViajeEnCurso(null);
      }
    };

    fetchUltimoViaje();
  }, [vehiculoSeleccionadoId]);

  const vehiculosFiltrados = vehiculos.filter((v) => {
    const t = busqueda.toLowerCase();
    return (
      v.patente?.toLowerCase().includes(t) ||
      v.modelo?.toLowerCase().includes(t)
    );
  });

  const handleSelectVehiculo = (vehiculo) => {
    if (vehiculoSeleccionadoId === vehiculo.idVehiculo) {
      setVehiculoSeleccionadoId(null);
      setViajeEnCurso(null);
      return;
    }

    setVehiculoSeleccionadoId(vehiculo.idVehiculo);
    setCentroMapa({
      lat: vehiculo.latitude,
      lng: vehiculo.longitude,
    });
  };

  return (
    <Box sx={{ height: "100%", width: "100%", position: "relative" }}>
      <MostrarMapa
        vehiculos={vehiculos}
        centro={centroMapa}
        seguimientoActivo={!!vehiculoSeleccionadoId}
        onDesactivarSeguimiento={() => {
          setVehiculoSeleccionadoId(null);
          setViajeEnCurso(null);
        }}
        viajeEnCurso={viajeEnCurso} // 👈 Pasamos el viaje activo al mapa
      />

      {/* BOTÓN FLOTANTE EXPANDIR/CONTRAER */}
      <Box
        sx={{
          position: "absolute",
          top: 20,
          right: modoPanel === "compacto" ? 20 : 340,
          zIndex: 20,
          transition: "all .3s"
        }}
      >
        <Tooltip title={modoPanel === "compacto" ? "Expandir" : "Contraer"}>
          <IconButton
            onClick={() =>
              setModoPanel(modoPanel === "compacto" ? "expandido" : "compacto")
            }
            sx={{
              width: 44,
              height: 44,
              borderRadius: "12px",
              backdropFilter: "blur(10px)",
              background: "rgba(15, 23, 42, 0.7)",
              color: "#fff",
              boxShadow: "0 8px 25px rgba(0,0,0,0.35)",
              "&:hover": {
                background: "rgba(15, 23, 42, 0.9)",
                transform: "scale(1.05)"
              }
            }}
          >
            {modoPanel === "compacto" ? <ChevronLeftIcon /> : <ChevronRightIcon />}
          </IconButton>
        </Tooltip>
      </Box>

      {/* PANEL LATERAL */}
      <Fade in={modoPanel !== "compacto"}>
        <Box
          sx={{
            position: "absolute",
            top: 10,
            right: 10,
            bottom: 10,
            width: 320,
            borderRadius: "20px",
            backdropFilter: "blur(18px)",
            background: "rgba(255,255,255,0.75)",
            boxShadow: "0 20px 50px rgba(0,0,0,0.25)",
            border: "1px solid rgba(255,255,255,0.4)",
            display: modoPanel === "compacto" ? "none" : "flex",
            flexDirection: "column",
            overflow: "hidden"
          }}
        >
          {/* HEADER */}
          <Box
            sx={{
              px: 2,
              py: 1.5,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center"
            }}
          >
            <TextField
              size="small"
              placeholder="Buscar vehículo..."
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              sx={{ flex: 1 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />

            <Tooltip title="Fijar panel">
              <IconButton
                onClick={() =>
                  setModoPanel(
                    modoPanel === "fijado" ? "expandido" : "fijado"
                  )
                }
              >
                <PushPinIcon
                  color={modoPanel === "fijado" ? "primary" : "inherit"}
                />
              </IconButton>
            </Tooltip>
          </Box>

          {/* LISTA DE CARDS */}
          <Box sx={{ flex: 1, overflowY: "auto", p: 1 }}>
            {vehiculosFiltrados.map((vehiculo) => (
              <CardVehiculos
                key={vehiculo.idVehiculo}
                vehiculo={vehiculo}
                compacto={false}
                onSelectVehiculo={handleSelectVehiculo}
                seleccionado={
                  vehiculoSeleccionadoId === vehiculo.idVehiculo
                }
              />
            ))}
          </Box>
        </Box>
      </Fade>

      {/* MODO COMPACTO (ICONOS FLOTANTES) */}
      {modoPanel === "compacto" && (
        <Box
          sx={{
            position: "absolute",
            top: 80,
            right: 12,
            display: "flex",
            flexDirection: "column",
            gap: 1,
            zIndex: 10
          }}
        >
          {vehiculosFiltrados.slice(0, 10).map((vehiculo) => (
            <CardVehiculos
              key={vehiculo.idVehiculo}
              vehiculo={vehiculo}
              compacto
              onSelectVehiculo={handleSelectVehiculo}
              seleccionado={
                vehiculoSeleccionadoId === vehiculo.idVehiculo
              }
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default MapaPage;